import { useEffect, useMemo, useState } from "react";
import apiClient from "../../services/apiClient";

type SummaryResponse = {
  ok: boolean;
  range: { from: string; to: string };
  totals: { totalEvents: number; distinctUsers: number };
  eventsByDay: { _id: string; count: number }[];
  eventsByName: { _id: string; count: number }[];
  featureUsage: { _id: string; count: number }[];
  subscriptionsByPlan: { _id: string; count: number }[];
};

const toYmd = (d: Date) => d.toISOString().slice(0, 10);

const AnalyticsDashboard = () => {
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toYmd(d);
  });
  const [to, setTo] = useState(() => toYmd(new Date()));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SummaryResponse | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<SummaryResponse>(
        `/analytics/summary?from=${from}&to=${to}`
      );
      setData(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = data?.totals ?? { totalEvents: 0, distinctUsers: 0 };

  const eventList = useMemo(() => data?.eventsByName ?? [], [data]);
  const featureList = useMemo(() => data?.featureUsage ?? [], [data]);
  const planList = useMemo(() => data?.subscriptionsByPlan ?? [], [data]);
  const byDay = useMemo(() => data?.eventsByDay ?? [], [data]);

  return (
    <div className="min-h-[70vh] p-8 pt-24 text-gray-900">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold">Owner Analytics</h1>
            <p className="text-gray-600 mt-1">
              Product usage signals across the app.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
            </div>
            <button
              onClick={load}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-500"
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-gray-500">Total Events</div>
            <div className="mt-2 text-3xl font-semibold">
              {totals.totalEvents}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-gray-500">Distinct Users</div>
            <div className="mt-2 text-3xl font-semibold">
              {totals.distinctUsers}
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold">Top Events</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {eventList.length === 0 && (
                <li className="text-gray-500">No data</li>
              )}
              {eventList.map((e) => (
                <li key={e._id} className="flex justify-between">
                  <span className="text-gray-700">{e._id}</span>
                  <span className="font-medium">{e.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold">Feature Usage</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {featureList.length === 0 && (
                <li className="text-gray-500">No data</li>
              )}
              {featureList.map((f) => (
                <li key={f._id || "unknown"} className="flex justify-between">
                  <span className="text-gray-700">{f._id || "unknown"}</span>
                  <span className="font-medium">{f.count}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold">Subscriptions</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {planList.length === 0 && (
                <li className="text-gray-500">No data</li>
              )}
              {planList.map((p) => (
                <li key={p._id || "unknown"} className="flex justify-between">
                  <span className="text-gray-700">{p._id || "unknown"}</span>
                  <span className="font-medium">{p.count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold">Events by Day</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {byDay.length === 0 && (
              <div className="text-sm text-gray-500">No data</div>
            )}
            {byDay.map((d) => (
              <div
                key={d._id}
                className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 text-sm"
              >
                <span className="text-gray-700">{d._id}</span>
                <span className="font-medium">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
